<template>
  <CCard>
    <CCardHeader>
      {{ $t('manageIp.Title') }}
    </CCardHeader>
    <CCardBody>
      <h4>{{ $t('manageIp.BanDecisionsCS') }}</h4>
      <hr>
      <div class="filter-container">
        <div class="filter-item">
          <label for="domain">{{ $t('manageIp.Domain') }}</label>
          <a-input id="domain" :placeholder="$t('manageIp.FilterByDomain')" v-model="filters.domain" />
        </div>
        <div class="filter-item">
          <label for="ip">{{ $t('manageIp.IP') }}</label>
          <a-input id="ip" :placeholder="$t('manageIp.FilterByIP')" v-model="filters.ip" />
        </div>

        <div class="filter-item">
          <label for="date">{{ $t('manageIp.FilterByDate') }}</label>
          <DateTimeRangePicker :model-value="filters.date" @update:modelValue="handleDateChange" />
        </div>

        <div class="filter-item checkbox-item" :class="{ 'checked': filters.is_active }">
          <a-checkbox id="is_active" v-model="filters.is_active">
            <span class="label">{{ $t('manageIp.FetchOnlyActiveBans') }}</span>
          </a-checkbox>
        </div>
      </div>
      <div class="filter-buttons">
        <a-button html-type="submit" type="primary" @click="applyFilters" :disabled="loading || isFilterEmpty">
          {{ $t('manageIp.Filter') }}
        </a-button>
        <a-button html-type="reset" type="default" @click="clearFilters" :disabled="loading || isFilterEmpty">
          {{ $t('manageIp.ClearFilters') }}
        </a-button>
        <a-button html-type="button" @click="applyFilters" :loading="loading">{{ $t('Refresh') }}</a-button>
        <div class="grow"></div>
        <a-button
          type="danger"
          @click="unbanSelected"
          :disabled="!hasActiveSelectedRecords"
          :loading="isUnbanLoading && selectedKeys.length > 0"
        >
          {{ $t('manageIp.UnbanSelected') }}
        </a-button>
      </div>
      <div class="unban-note">
        <a-alert
          type="info"
          :message="$t('manageIp.Note')"
          :description="$t('manageIp.OnlyActiveBansCanBeUnbanned')"
          show-icon
          closable
        />
      </div>
      <a-table
        :columns="columns"
        :data-source="data"
        class="ip-table"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 'max-content', scrollToFirstRowOnChange: true }"
        :row-selection="{ selectedRowKeys: selectedKeys, onChange: onRowSelectionChange }"
        :row-key="record => record.id || record.ip"
        @change="handleTableChange"
      >
        <span slot="action" slot-scope="text, record">
          <a-button
            type="danger"
            size="small"
            @click="unban(record)"
            :disabled="!record.is_active || selectedKeys.length > 0"
            :loading="unbanLoading[record.id]">
            {{ $t('manageIp.Unban') }}
          </a-button>
        </span>
        <span
          slot="bantime" slot-scope="text"
          :class="{ 'manual-banned': text.startsWith('-') }"
          :title="text"
        >
          {{ text ? (text.startsWith('-') ? $t('manageIp.ManuallyUnBanned') : text) : '' }}
        </span>
        <span slot="status" slot-scope="text">
          <a-tag :color="text ? 'green' : 'red'">
            {{ text ? $t('manageIp.Active') : $t('manageIp.Inactive') }}
          </a-tag>
        </span>
      </a-table>

      <template v-if="gaiusBanData.length > 0">
        <h4 class="mt-4">{{ $t('manageIp.BanDecisionsL7') }}</h4>
        <hr>
        <a-table
          :columns="columns"
          :data-source="gaiusBanData"
          class="ip-table"
          :loading="gaiusBanLoading"
          :pagination="gaiusBanPaginationConfig"
          :scroll="{ x: 'max-content', scrollToFirstRowOnChange: true }"
          :row-key="record => record.id || record.ip"
          @change="handleGaiusBanTableChange"
        >
          <span slot="action" slot-scope="text, record">
            <a-button type="danger" size="small" @click="unbanGaiusBan(record)" :disabled="!record.is_active"
              :loading="unbanLoading[record.id]">
              {{ $t('manageIp.Unban') }}
            </a-button>
          </span>
        </a-table>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import axios from "@/plugins/axios.js"
import moment from 'moment-timezone';

const defaultFilters = {
  domain: '',
  ip: '',
  is_active: undefined,
  date: [null, null],
  page: 1,
  perPage: 10,
};

export default {
  components: {
    DateTimeRangePicker: () => import('@/components/DateTimeRangePicker.vue')
  },
  data() {
    return {
      data: [],
      totalDataSize: 0,
      filters: Object.assign({}, defaultFilters),
      selectedKeys: [],
      loading: false,
      unbanLoading: {},
      columns: null,
      // Gaius Ban (CS or L7) table data
      gaiusBanData: [],
      gaiusBanTotalSize: 0,
      gaiusBanLoading: false,
      gaiusBanPage: 1,
      gaiusBanPerPage: 10,
    };
  },
  created() {
    this.initializeColumns();
    this.fetchDomainAttack();
    this.fetchGaiusBanDecisions();
  },
  computed: {
    paginationConfig() {
      const me = this;
      return {
        disabled: this.loading,
        current: this.filters.page,
        pageSize: this.filters.perPage,
        total: this.totalDataSize,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: function(total, range) {
          return me.$t('manageIp.PaginationTotal', { start: range[0], end: range[1], total: total });
        },
        showTitle: true,
        pageSizeOptions: ['10', '25', '50', '100']
      };
    },
    isFilterEmpty() {
      return Object.entries(this.filters).every(([key, value]) => {
        // Skip pagination fields
        if (key === 'page' || key === 'perPage') {
          return true;
        }
        // Handle array (date range)
        if (Array.isArray(value)) {
          return !value[0] && !value[1];
        }
        // Handle undefined/null/empty string
        return value === undefined || value === null || value === '';
      });
    },
    isUnbanLoading() {
      return Object.values(this.unbanLoading).some(value => value);
    },
    hasActiveSelectedRecords() {
      return this.selectedKeys.some(key => {
        const record = this.data.find(item => item.id === key);
        return record && record.is_active === true;
      });
    },
    gaiusBanPaginationConfig() {
      const me = this;
      return {
        disabled: this.gaiusBanLoading,
        current: this.gaiusBanPage,
        pageSize: this.gaiusBanPerPage,
        total: this.gaiusBanTotalSize,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: function(total, range) {
          return me.$t('manageIp.PaginationTotal', { start: range[0], end: range[1], total: total });
        },
        showTitle: true,
        pageSizeOptions: ['10', '25', '50', '100']
      };
    },
  },
  methods: {
    applyFilters() {
      this.fetchDomainAttack();
    },
    clearFilters() {
      this.filters = { ...defaultFilters };
      this.fetchDomainAttack();
    },
    onRowSelectionChange(v) {
      this.selectedKeys = v;
    },
    handleTableChange(pagination) {
      this.filters.page = pagination.current;
      this.filters.perPage = pagination.pageSize;
      this.applyFilters();
    },
    fetchDomainAttack() {
      this.loading = true;
      const params = {
        host: this.filters.domain || undefined,
        ip: this.filters.ip || undefined,
        is_active: this.filters.is_active !== undefined ? this.filters.is_active : undefined,
        page: this.filters.page || undefined,
        perpage: this.filters.perPage || undefined,
      };

      // Add date range to params -> converting moment objects to ISO strings and UTC tz
      if (
        this.filters.date && this.filters.date.length === 2 &&
        this.filters.date[0] && this.filters.date[1]
      ) {
        params.date_from = moment(this.filters.date[0]).utc().format("YYYY-MM-DDTHH:mm:ss");
        params.date_to = moment(this.filters.date[1]).utc().format("YYYY-MM-DDTHH:mm:ss");
      }
      
      // Remove undefined values to keep params clean
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      
      axios.get('attack/decisions/', { params })
        .then(response => {
          this.data = response.data.result || [];

          // .created is a utc datetime string, convert it to browser timezone
          this.data.forEach(item => {
            item.created = moment.utc(item.created).local().format("llll");
          });

          this.totalDataSize = response.data.count || 0;
        })
        .catch(error => {
          console.error('Error fetching domain attack data:', error);
          const errorMessage = error?.response?.data?.message || error?.message || this.$t('ErrorOccurredWith', ['Unknown error']);
          this.flash(errorMessage, 'error', { timeout: 3000 });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getUnbanPayload(record) {
      return {
        id: record.type === 'cs' ? record.id : record.ip,
        type: record.type,
      };
    },
    unban(record) {
      if (!record.is_active) {
        return;
      }
      this.$set(this.unbanLoading, record.id, true);
      this.performUnban([this.getUnbanPayload(record)])
        .finally(() => {
          this.$set(this.unbanLoading, record.id, false);
        });
    },
    unbanSelected() {
      const selectedDecisions = this.selectedKeys.map(key => this.getUnbanPayload(
        this.data.find(item => item.id === key)
      ));
      
      // Set loading state for all records
      this.selectedKeys.forEach(id => {
        this.$set(this.unbanLoading, id, true);
      });
      
      this.performUnban(selectedDecisions, this.selectedKeys);
    },
    performUnban(decisions, recordIds) {
      return axios.post('attack/decisions/', { decision_ids: decisions })
        .then(() => {
          // Clear loading state
          if (recordIds) {
            recordIds.forEach(id => {
              this.$set(this.unbanLoading, id, false);
            });
          }
          this.selectedKeys = [];
          this.fetchDomainAttack();
          this.flash(this.$t('message.UnbanActionSuccessful'), 'success', { timeout: 3000 });
        })
        .catch(error => {
          // Clear loading state on error
          if (recordIds) {
            recordIds.forEach(id => {
              this.$set(this.unbanLoading, id, false);
            });
          }
          console.error("Error while attempting to unban:", error);
          const errorMessage = error?.response?.data?.message;
          if (errorMessage) {
            this.flash(
              this.$t('message.UnbanActionFailedMessage', { msg: errorMessage }),
              'error',
              { timeout: 3000 }
            );
          } else {
            this.flash(
              this.$t('message.UnbanActionFailed'),
              'error',
              { timeout: 3000 }
            );
          }
        });
    },
    handleDateChange(val) {
      this.filters.date = val;
    },
    initializeColumns() {
      this.columns = [
        {
          title: this.$t('manageIp.IP'),
          dataIndex: 'ip',
          key: 'ip'
        },
        {
          title: this.$t('manageIp.Domain'),
          dataIndex: 'domain',
          key: 'domain'
        },
        {
          title: this.$t('manageIp.Rule/Reason'),
          dataIndex: 'reason',
          key: 'reason'
        },
        {
          title: this.$t('manageIp.Status'),
          dataIndex: 'is_active',
          key: 'is_active',
          scopedSlots: { customRender: 'status' }
        },
        {
          title: this.$t('manageIp.BannedDate'),
          dataIndex: 'created',
          key: 'created'
        },
        {
          title: this.$t('manageIp.BannedTime'),
          dataIndex: 'bantime',
          key: 'bantime',
          scopedSlots: { customRender: 'bantime' }
        },
        {
          title: this.$t('manageIp.Action'),
          dataIndex: 'action',
          key: 'action',
          scopedSlots: {
            customRender: 'action'
          }
        }
      ];
    },
    fetchGaiusBanDecisions() {
      this.gaiusBanLoading = true;
      const params = {
        type: 'gaius-ban',
        page: this.gaiusBanPage || undefined,
        perpage: this.gaiusBanPerPage || undefined,
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      
      axios.get('attack/decisions/', { params })
        .then(response => {
          this.gaiusBanData = response.data.result || [];
          this.gaiusBanTotalSize = response.data.count || 0;
        })
        .catch(error => {
          console.error('Error fetching gaius-ban decisions:', error);
          const errorMessage = error?.response?.data?.message || error?.message || this.$t('ErrorOccurredWith', ['Unknown error']);
          this.flash(errorMessage, 'error', { timeout: 3000 });
        })
        .finally(() => {
          this.gaiusBanLoading = false;
        });
    },
    unbanGaiusBan(record) {
      if (!record.is_active) {
        return;
      }
      this.$set(this.unbanLoading, record.id, true);
      this.performUnban([this.getUnbanPayload(record)], [record.id])
        .then(() => {
          this.fetchGaiusBanDecisions();
        })
        .finally(() => {
          this.$set(this.unbanLoading, record.id, false);
        });
    },
    handleGaiusBanTableChange(pagination) {
      this.gaiusBanPage = pagination.current;
      this.gaiusBanPerPage = pagination.pageSize;
      this.fetchGaiusBanDecisions();
    },
  }
}
</script>

<style lang="scss" scoped>
@media (max-width: 1140px) {
  .filter-container {
    flex-wrap: wrap;

    .filter-item {
      width: 100%;
    }

    margin-bottom: 1rem !important;
  }

  .filter-buttons {
    flex-wrap: wrap;
  }
}

.filter-container {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: end;


  .filter-item {
    flex-grow: 1;
    flex-shrink: 0;

    label {
      color: #000;
    }

    input {
      width: 100%;
    }
  }

  .checkbox-item {
    border: 1px solid #d9d9d9;
    padding: 4.5px 10px;
    border-radius: 5px;
    height: 100%;

    &:hover {
      border-color: var(--info);

      span.label {
        color: #000;
      }
    }

    &.checked {
      border-color: var(--info);
    }

    span.label {
      color: #585858;
      transition: color 0.3s ease;
    }
  }
}

.filter-buttons {
  width: 100%;
  display: flex;
  margin-bottom: 1rem;
  gap: 10px;

  .grow {
    flex-grow: 1;
  }
}

.unban-note {
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.manual-banned {
  color: var(--danger);
}
</style>